import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  User, 
  Mail, 
  Trash2, 
  Edit3, 
  CheckCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';
import { tenantAdminAPI } from '../services/api/tenantAdminAPI';
import toast from 'react-hot-toast';

const ScheduledEmailsList = () => {
  const [scheduledEmails, setScheduledEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadScheduledEmails();
  }, []);

  const loadScheduledEmails = async () => {
    try {
      setLoading(true);
      const response = await tenantAdminAPI.getScheduledEmails();
      setScheduledEmails(response.data.scheduled_emails || []);
    } catch (error) {
      console.error('Failed to load scheduled emails:', error);
      toast.error('Failed to load scheduled emails');
    } finally {
      setLoading(false);
    }
  };

  const refreshEmails = async () => {
    try {
      setRefreshing(true);
      await loadScheduledEmails();
      toast.success('Scheduled emails refreshed');
    } catch (error) {
      console.error('Failed to refresh emails:', error);
      toast.error('Failed to refresh emails');
    } finally {
      setRefreshing(false);
    }
  };

  const cancelScheduledEmail = async (jobId, patientName) => {
    if (!window.confirm(`Are you sure you want to cancel the scheduled email for ${patientName}?`)) {
      return;
    }

    try {
      await tenantAdminAPI.cancelScheduledEmail(jobId);
      toast.success('Scheduled email cancelled successfully');
      loadScheduledEmails(); // Refresh the list
    } catch (error) {
      console.error('Failed to cancel scheduled email:', error);
      toast.error('Failed to cancel scheduled email');
    }
  };

  const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return 'N/A';
    
    try {
      const date = new Date(dateTimeString);
      // Convert to Indian timezone (IST - UTC+5:30)
      return date.toLocaleString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata'
      });
    } catch (error) {
      return dateTimeString;
    }
  };

  const getStatusColor = (nextRunTime) => {
    if (!nextRunTime) return 'text-gray-500';
    
    const now = new Date();
    const scheduledTime = new Date(nextRunTime);
    
    if (scheduledTime < now) {
      return 'text-red-500'; // Past due
    } else if (scheduledTime - now < 24 * 60 * 60 * 1000) {
      return 'text-yellow-500'; // Within 24 hours
    } else {
      return 'text-green-500'; // Future
    }
  };

  const getStatusIcon = (nextRunTime) => {
    if (!nextRunTime) return <AlertCircle className="h-4 w-4" />;
    
    const now = new Date();
    const scheduledTime = new Date(nextRunTime);
    
    if (scheduledTime < now) {
      return <AlertCircle className="h-4 w-4" />; // Past due
    } else if (scheduledTime - now < 24 * 60 * 60 * 1000) {
      return <Clock className="h-4 w-4" />; // Within 24 hours
    } else {
      return <CheckCircle className="h-4 w-4" />; // Future
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Loading scheduled emails...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Mail className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-medium text-gray-900">Scheduled Recording Emails</h3>
            <span className="bg-blue-100 text-blue-800 text-xs font-medium px-2.5 py-0.5 rounded-full">
              {scheduledEmails.length}
            </span>
          </div>
          <button
            onClick={refreshEmails}
            disabled={refreshing}
            className="flex items-center space-x-2 px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        {scheduledEmails.length === 0 ? (
          <div className="text-center py-8">
            <Mail className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h4 className="text-lg font-medium text-gray-900 mb-2">No Scheduled Emails</h4>
            <p className="text-gray-600">
              No recording invitation emails are currently scheduled.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {scheduledEmails.map((email) => (
              <div
                key={email.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`flex items-center space-x-1 ${getStatusColor(email.next_run_time)}`}>
                        {getStatusIcon(email.next_run_time)}
                        <span className="text-sm font-medium">
                          {email.next_run_time ? 
                            (new Date(email.next_run_time) < new Date() ? 'Past Due' : 'Scheduled') : 
                            'Unknown Status'
                          }
                        </span>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-3">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {email.patient_name || 'Unknown Patient'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {email.patient_email || 'No email'}
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Calendar className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatDateTime(email.next_run_time)}
                          </p>
                          <p className="text-xs text-gray-500">
                            Scheduled Time (IST)
                          </p>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-500" />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {email.next_run_time ? 
                              (() => {
                                const now = new Date();
                                const scheduled = new Date(email.next_run_time);
                                const diff = scheduled - now;
                                
                                if (diff < 0) {
                                  return 'Past due';
                                } else if (diff < 60 * 60 * 1000) {
                                  return `${Math.floor(diff / (60 * 1000))}m`;
                                } else if (diff < 24 * 60 * 60 * 1000) {
                                  return `${Math.floor(diff / (60 * 60 * 1000))}h`;
                                } else {
                                  return `${Math.floor(diff / (24 * 60 * 60 * 1000))}d`;
                                }
                              })() : 'Unknown'
                            }
                          </p>
                          <p className="text-xs text-gray-500">
                            Time Remaining
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => cancelScheduledEmail(email.id, email.patient_name)}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      title="Cancel scheduled email"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduledEmailsList;
